/**
 * Admin V3 — Pricing — ConfirmationModal
 *
 * Final guard before PUT /pricing/config. Renders the diff (computed by
 * diffScheduleA) and requires the operator to:
 *   1. Acknowledge customer-fare impact (checkbox)
 *   2. Acknowledge 5-min cache TTL (checkbox) — Gate G2
 *   3. Type "CONFIRMER" (case-sensitive)
 *
 * Submission is gated until all three pass. Loading state locks the modal
 * until the response arrives (no double-click). Errors render inline; the
 * modal stays open so the operator can correct and retry.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, X, ArrowRight } from 'lucide-react';
import { cn } from '../../shared/visualTokens';

const labels = {
  title:           'Confirmer la mise à jour Schedule A',
  diffTitle:       'Modifications à appliquer',
  noDiff:          'Aucun changement détecté.',
  impactCheckbox:  "Je reconnais que ces modifications s'appliquent à TOUS les nouveaux devis client à partir du moment où le cache se renouvelle.",
  ttlCheckbox:     'Je comprends que le cache du moteur a une durée de vie de 5 minutes (Gate G2) : la propagation peut prendre jusqu\'à 5 minutes en production.',
  confirmLabel:    'Pour confirmer, tapez exactement CONFIRMER en majuscules.',
  confirmKeyword:  'CONFIRMER',
  cancel:          'Annuler',
  submit:          'Confirmer la mise à jour',
  submitting:      'Enregistrement…',
  errorTitle:      'Erreur',
  pathPrefix:      '',
};

const Bullet = ({ entry }) => (
  <li className="flex items-start gap-2 py-1.5" data-testid={`adminv3-pricing-modal-diff-${entry.path}`}>
    <ArrowRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
    <div className="flex flex-col text-[12.5px] leading-relaxed">
      <span className="font-mono text-[11.5px] text-slate-500">{entry.path}</span>
      <div className="flex items-baseline gap-1.5 text-slate-700">
        <span className="rounded bg-rose-50 px-1.5 py-0.5 font-mono text-[11.5px] text-rose-700">
          {String(entry.before ?? '—')}
        </span>
        <ArrowRight className="h-3 w-3 text-slate-400" />
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[11.5px] text-emerald-700 font-semibold">
          {String(entry.after ?? '—')}
        </span>
      </div>
    </div>
  </li>
);

/**
 * Props:
 *   open            — boolean
 *   diff            — array of {path, before, after} entries (from diffScheduleA)
 *   submitting      — boolean (mutation in flight)
 *   errorMessage    — optional string to render inline
 *   onClose()       — close handler (blocked while submitting)
 *   onConfirm()     — final submission handler
 */
const ConfirmationModal = ({ open, diff, submitting, errorMessage, onClose, onConfirm }) => {
  const [impactAck, setImpactAck] = useState(false);
  const [ttlAck, setTtlAck]       = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const initialFocusRef = useRef(null);

  useEffect(() => {
    if (open) {
      setImpactAck(false);
      setTtlAck(false);
      setConfirmText('');
    }
  }, [open]);

  useEffect(() => {
    if (open && initialFocusRef.current) initialFocusRef.current.focus();
  }, [open]);

  const safeClose = useCallback(() => {
    if (submitting) return;
    onClose && onClose();
  }, [submitting, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') safeClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, safeClose]);

  const hasDiff = Array.isArray(diff) && diff.length > 0;
  const isConfirmTyped = confirmText === labels.confirmKeyword;
  const canSubmit = open && !submitting && hasDiff && impactAck && ttlAck && isConfirmTyped;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="adminv3-pricing-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) safeClose(); }}
      data-testid="adminv3-pricing-confirmation-modal"
    >
      <div className="relative w-full max-w-lg mx-4 rounded-[24px] bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.18)]">
        <button
          type="button"
          aria-label={labels.cancel}
          onClick={safeClose}
          disabled={submitting}
          className={cn(
            'absolute right-4 top-4 rounded-full p-1.5 text-slate-400 transition',
            submitting ? 'cursor-not-allowed opacity-30' : 'hover:bg-slate-100 hover:text-slate-700',
          )}
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id="adminv3-pricing-modal-title"
          ref={initialFocusRef}
          tabIndex={-1}
          className="mb-4 pr-6 text-[16px] font-semibold text-slate-900 outline-none"
        >
          {labels.title}
        </h2>

        {/* Diff */}
        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <h3 className="mb-1.5 text-[12.5px] font-semibold uppercase tracking-wide text-slate-500">
            {labels.diffTitle}
          </h3>
          {hasDiff ? (
            <ul className="max-h-56 overflow-y-auto divide-y divide-slate-200" data-testid="adminv3-pricing-modal-diff-list">
              {diff.map((d) => (
                <Bullet key={d.path} entry={d} />
              ))}
            </ul>
          ) : (
            <p className="py-2 text-center text-[12px] italic text-slate-500">{labels.noDiff}</p>
          )}
        </div>

        {/* Impact acknowledgment */}
        <label className="mb-2 flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={impactAck}
            onChange={(e) => setImpactAck(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-emerald-600"
            data-testid="adminv3-pricing-modal-impact-checkbox"
          />
          <span className="text-[12.5px] leading-relaxed text-slate-700">{labels.impactCheckbox}</span>
        </label>

        {/* TTL acknowledgment */}
        <label className="mb-3 flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={ttlAck}
            onChange={(e) => setTtlAck(e.target.checked)}
            disabled={submitting}
            className="mt-0.5 h-4 w-4 flex-shrink-0 accent-emerald-600"
            data-testid="adminv3-pricing-modal-ttl-checkbox"
          />
          <span className="text-[12.5px] leading-relaxed text-slate-700">{labels.ttlCheckbox}</span>
        </label>

        {/* CONFIRMER text gate */}
        <div className="mb-4">
          <label className="mb-1 block text-[12px] font-semibold text-slate-700">{labels.confirmLabel}</label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={labels.confirmKeyword}
            disabled={submitting}
            spellCheck={false}
            autoComplete="off"
            className={cn(
              'w-full rounded-xl border bg-white px-3 py-2 text-[13px] text-slate-900 outline-none disabled:opacity-50',
              isConfirmTyped ? 'border-emerald-400' : 'border-slate-200 focus:border-slate-400',
            )}
            data-testid="adminv3-pricing-modal-confirm-input"
          />
        </div>

        {/* Inline error */}
        {errorMessage && (
          <div
            className="mb-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5"
            data-testid="adminv3-pricing-modal-error"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-600" />
            <div className="text-[12px] leading-relaxed text-rose-700">
              <div className="font-semibold">{labels.errorTitle}</div>
              <div>{errorMessage}</div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={safeClose}
            disabled={submitting}
            className="rounded-xl px-4 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            data-testid="adminv3-pricing-modal-cancel"
          >
            {labels.cancel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canSubmit}
            className={cn(
              'flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-semibold text-white transition',
              canSubmit ? 'bg-emerald-600 hover:bg-emerald-700' : 'cursor-not-allowed bg-slate-300',
            )}
            data-testid="adminv3-pricing-modal-confirm"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {submitting ? labels.submitting : labels.submit}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
